import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, ComposedChart,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import SectionHeader from "../components/ui/SectionHeader";
import ChartCard from "../components/ui/ChartCard";
import MetricCard from "../components/ui/MetricCard";
import ChartTooltip from "../components/ui/ChartTooltip";
import { useTranslate } from "../i18n/useTranslate";
import { transportData, trafficCorridors, trainSchedule } from "../data/mockData";
import { googleColor } from "../data/googleColors";
import { chartColor, gridProps, axisTick, legendStyle } from "../data/chartTheme";
import { STATUS, statusHex, statusClasses, thresholdStatus } from "../data/statusColors";
import { useIntelligence } from "../intelligence/useIntelligence";
import { LiveMobilitySection } from "../components/live/LiveOpsSections";
import { StaticContentDivider } from "../components/live/primitives";
import {
  Train, Bus, Car, Clock, AlertTriangle, TrendingUp, Navigation,
  MapPin, Zap, CheckCircle, Radio, ArrowRight, Users, Bike
} from "lucide-react";

// ── inline data ──────────────────────────────────────────────────
const PARKING_DATA = [
  { name:"P1 — Nashik Road Stn", total:2400, used:2100, pct:87, ev:12,  overflow:"P2 — Panchvati",   shuttle:"SR-02 every 12 min" },
  { name:"P2 — Panchvati",       total:1800, used:1240, pct:69, ev:8,   overflow:"P3 — Gadkari",     shuttle:"SP-04 every 15 min" },
  { name:"P3 — Gadkari Chowk",   total:3200, used:2890, pct:90, ev:18,  overflow:"P5 — Gangapur",    shuttle:"GC-01 every 10 min" },
  { name:"P4 — Trimbak Road",    total:1600, used:980,  pct:61, ev:6,   overflow:"P1 — Nashik Rd",   shuttle:"TR-06 every 20 min" },
  { name:"P5 — Gangapur Camp",   total:2000, used:1420, pct:71, ev:10,  overflow:"Open field OFP-1", shuttle:"GC-02 every 18 min" },
];

const PARK_AND_RIDE = [
  { id:"PR-1", name:"Nashik Road PR",    capacity:3200, used:2680, pct:84, shuttle:"SR-01",      freq:"Every 8 min", dest:"Ramkund / Panchvati" },
  { id:"PR-2", name:"Deolali Camp PR",   capacity:2800, used:1960, pct:70, shuttle:"DC-01/02",   freq:"Every 15 min",dest:"Trimbakeshwar" },
  { id:"PR-3", name:"Satpur MIDC PR",    capacity:2400, used:1440, pct:60, shuttle:"SM-01",      freq:"Every 12 min",dest:"Kalaram / Panchvati" },
  { id:"PR-4", name:"NH3 Igatpuri PR",   capacity:4000, used:2400, pct:60, shuttle:"NH3-01/02",  freq:"Every 20 min",dest:"All zones via relay" },
];

const BUS_DISPATCH = [
  { busNo:"MSRTC SR-01", route:"Nashik Rd → Ramkund",        bay:"Bay 2", status:"On Route",  eta:"14:38", load:94, type:"Express" },
  { busNo:"MSRTC SR-02", route:"Panchvati → Ramkund loop",   bay:"Bay 3", status:"Boarding",  eta:"14:42", load:78, type:"Circular" },
  { busNo:"MSRTC TR-04", route:"Nashik Rd → Trimbakeshwar",  bay:"Bay 9", status:"On Route",  eta:"15:10", load:88, type:"Express" },
  { busNo:"MSRTC SP-03", route:"Gadkari → Panchvati",        bay:"Bay 5", status:"Delayed",   eta:"14:55", load:72, type:"Shuttle"  },
  { busNo:"MSRTC GC-01", route:"Nashik Rd → Gangapur",       bay:"Bay 7", status:"On Route",  eta:"14:44", load:65, type:"Shuttle"  },
  { busNo:"MSRTC KT-01", route:"Panchvati → Kalaram loop",   bay:"Bay 4", status:"Boarding",  eta:"14:50", load:82, type:"Circular" },
  { busNo:"MSRTC NH3-01",route:"Nashik → Igatpuri Park+Ride",bay:"Bay 1", status:"Departed",  eta:"Done",  load:91, type:"Express"  },
  { busNo:"MSRTC IB-08", route:"Mumbai CST → Nashik (IC)",   bay:"Bay 11",status:"En Route",  eta:"16:20", load:98, type:"Intercity"},
];

const SHUTTLE_CIRCUITS = [
  { route:"Nashik Rd ↔ Ramkund",      frequency:"8 min",  vehicles:12, passengers:4840, status:"Running",  avgTime:"18 min" },
  { route:"Panchvati ↔ Kalaram loop", frequency:"10 min", vehicles:8,  passengers:2960, status:"Running",  avgTime:"14 min" },
  { route:"Nashik Rd ↔ Trimbakeshwar",frequency:"15 min", vehicles:6,  passengers:2180, status:"Delayed",  avgTime:"38 min" },
  { route:"Gadkari ↔ Ramkund",        frequency:"12 min", vehicles:10, passengers:3620, status:"Running",  avgTime:"22 min" },
  { route:"Gangapur ↔ Panchvati",     frequency:"20 min", vehicles:4,  passengers:1240, status:"Running",  avgTime:"28 min" },
];

const SIGNAL_OVERRIDES = [
  { junction:"Godavari Bridge North",  override:"Green extended +90s", reason:"Ambulance corridor — AMB-07", active:true  },
  { junction:"Gadkari Chowk",          override:"All red 45s cycle",   reason:"Crowd crossing — peak flow",  active:true  },
  { junction:"Nashik Road — NH3",      override:"Bus priority phase",  reason:"MSRTC fleet dispatch",        active:true  },
  { junction:"Trimbak Road Junction",  override:"Normal",              reason:"—",                           active:false },
  { junction:"Panchvati Bridge South", override:"Green extended +60s", reason:"Pedestrian surge",            active:true  },
];

const DELAY_CAUSES = [
  { name:"Crowd overflow on road", value:38 },
  { name:"Vehicle breakdown",      value:24 },
  { name:"Ambulance priority",     value:18 },
  { name:"VIP/Protocol movement",  value:12 },
  { name:"Weather / road surface", value:8  },
];

const INBOUND_OUTBOUND = [
  { time:"06:00", inbound:42000, outbound:8000  },
  { time:"07:00", inbound:88000, outbound:14000 },
  { time:"08:00", inbound:124000,outbound:22000 },
  { time:"09:00", inbound:156000,outbound:36000 },
  { time:"10:00", inbound:168000,outbound:52000 },
  { time:"11:00", inbound:148000,outbound:68000 },
  { time:"12:00", inbound:112000,outbound:84000 },
  { time:"13:00", inbound:84000, outbound:94000 },
  { time:"14:00", inbound:72000, outbound:112000},
];

const LOAD_FORECAST = [
  { time:"12:00", railway:72, bus:78, shuttle:74, parking:71 },
  { time:"13:00", railway:76, bus:82, shuttle:78, parking:74 },
  { time:"14:00", railway:80, bus:86, shuttle:82, parking:76 },
  { time:"15:00", railway:84, bus:90, shuttle:86, parking:80 },
  { time:"16:00", railway:88, bus:94, shuttle:88, parking:84 },
  { time:"17:00", railway:82, bus:88, shuttle:84, parking:78 },
  { time:"18:00", railway:68, bus:74, shuttle:70, parking:64 },
  { time:"19:00", railway:56, bus:62, shuttle:58, parking:52 },
];

const PEDESTRIAN_FLOWS = [
  { corridor:"Godavari Bridge walkway",   density:"8.4/m²", flow:2800, status:"Critical",  action:"Barrier deployed" },
  { corridor:"Ramkund Ghat approach",     density:"7.8/m²", flow:2400, status:"High",      action:"PA diversion" },
  { corridor:"Panchvati Steps S-4 to S-8",density:"6.4/m²", flow:1960, status:"Moderate",  action:"Monitoring" },
  { corridor:"Trimbakeshwar Main Road",   density:"5.2/m²", flow:1480, status:"Moderate",  action:"Normal" },
  { corridor:"Nashik Rd Station exit",    density:"4.1/m²", flow:1120, status:"Normal",    action:"Normal" },
];

const NIGHT_TRANSPORT = [
  { service:"Last MSRTC to Mumbai",    time:"23:45", bay:"Bay 1",  seats:400, booked:312, status:"Confirmed" },
  { service:"Night shuttle — Ramkund", time:"22:30–04:00", bay:"Bay 3", seats:"Continuous", booked:"—", status:"Active" },
  { service:"Emergency auto fleet",    time:"00:00–06:00", bay:"All zones", seats:120, booked:"—", status:"Reserved" },
  { service:"Train — Tapovan 12533",   time:"04:28", platform:"2",  seats:920, booked:680, status:"Confirmed" },
  { service:"Train — Panchavati 12102",time:"05:15", platform:"3",  seats:1120,booked:820, status:"Confirmed" },
];

const corridorStatusBadge = s =>
  s === "Critical" ? "badge badge-critical" : s === "Heavy" ? "badge badge-high" :
  s === "Moderate" ? "badge badge-warning" : "badge badge-success";

// Bus dispatch status -> shared STATUS bucket (exact class match to the old
// local hex-per-status chain, now sourced from statusColors.js).
const busStatusBucket = s =>
  s === "On Route"  ? "good"     :
  s === "Boarding"  ? "info"     :
  s === "Delayed"   ? "critical" :
  s === "Departed"  ? "neutral"  : "warning";

// Shared load/pct threshold bands used by several widgets below (traffic
// corridor bars, bus load, parking pct, train load).
const LOAD_BANDS_4 = [{ min:88, bucket:"critical" }, { min:75, bucket:"serious" }, { min:60, bucket:"warning" }, { min:0, bucket:"good" }];
const LOAD_BANDS_3 = [{ min:90, bucket:"critical" }, { min:75, bucket:"warning" }, { min:0, bucket:"good" }];
const LOAD_BANDS_2 = [{ min:80, bucket:"warning" }, { min:0, bucket:"good" }];

// Pedestrian corridor status -> shared STATUS bucket.
const pedestrianBucket = s =>
  s === "Critical" ? "critical" :
  s === "High"     ? "serious"  :
  s === "Moderate" ? "warning"  : "good";

// ── component ────────────────────────────────────────────────────
export default function MobilityIntelligence() {
  const { mobility } = useIntelligence({ running: false });
  const [activeTab, setActiveTab] = useState("buses");
  const t = useTranslate();

  // Computed directly from the arrays rendered below, instead of separately
  // hand-typed numbers that can drift from what the tables actually show.
  // "Elevated" corridors = Critical/Heavy/High, the three tiers the badge
  // helper below already treats as needing attention.
  const elevatedCorridors = trafficCorridors.filter(c => ["Critical","Heavy","High"].includes(c.status)).length;
  const activeSignalOverrides = SIGNAL_OVERRIDES.filter(s => s.active).length;
  const totalDelayedServices = transportData.bus.delayed + transportData.railway.delayed + transportData.shuttle.delayed;
  const parkingSpacesTotal = PARKING_DATA.reduce((s, p) => s + p.total, 0);
  const parkingUsedTotal   = PARKING_DATA.reduce((s, p) => s + p.used, 0);
  const parkingEvTotal     = PARKING_DATA.reduce((s, p) => s + p.ev, 0);
  const inboundTotal  = INBOUND_OUTBOUND.reduce((s, r) => s + r.inbound, 0);
  const outboundTotal = INBOUND_OUTBOUND.reduce((s, r) => s + r.outbound, 0);
  const netPresentLakh = ((inboundTotal - outboundTotal) / 100000).toFixed(1);

  return (
    <div className="space-y-8">
      <SectionHeader icon="Navigation" title="Transport & Mobility Intelligence"
        subtitle="360° railway · MSRTC · shuttle circuits · parking · park-and-ride · pedestrian flow · signal overrides · inbound/outbound balance · delay analysis · night transport · Kumbh 2027 Nashik"
      />


      <LiveMobilitySection mobility={mobility} />

      <StaticContentDivider />
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <MetricCard title="Railway Load"    value={`${transportData.railway.load}%`}  icon="Train"         color="blue"   index={0} subtitle={`${transportData.railway.trains} trains`} />
        <MetricCard title="Bus Load"        value={`${transportData.bus.load}%`}      icon="Bus"           color="amber"  index={1} subtitle={`${transportData.bus.buses} active`} />
        <MetricCard title="Shuttle Load"    value={`${transportData.shuttle.load}%`}  icon="Car"           color="green"  index={2} subtitle={`${transportData.shuttle.shuttles} circuits`} />
        <MetricCard title="Parking Load"    value={`${transportData.parking.load}%`}  icon="ParkingCircle" color="purple" index={3} subtitle={`${transportData.parking.full} zones full`} />
        <MetricCard title="Corridors High"  value={elevatedCorridors.toString()}       icon="AlertTriangle" color="red"    index={4} live />
        <MetricCard title="Delayed Services"value={totalDelayedServices.toString()}    icon="Clock" color="amber" index={5} />
        <MetricCard title="Signal Overrides"value={activeSignalOverrides.toString()}   icon="Zap"           color="amber"  index={6} />
        <MetricCard title="Park+Ride Zones" value={PARK_AND_RIDE.length.toString()}    icon="MapPin"        color="blue"   index={7} />
      </div>

      {/* Transport Command Banner */}
      <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white border border-slate-200 shadow-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full bg-amber-400 live-dot" />
              <span className="text-[10px] font-black text-saffron-dark tracking-widest">{t("TRANSPORT COMMAND — 14:33 IST — ALL MODES ACTIVE")}</span>
            </div>
            <h3 className="text-base font-black text-slate-900">{t("Maha Kumbh AI Transport & Mobility Command — Simhastha 2027")}</h3>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">
              {t("38 trains · 210 MSRTC buses · 120 shuttles · 52 parking zones · 4 park-and-ride · 4 signal overrides active · AI load forecast: peak 16:00 · Inbound now declining — outbound surge expected 15:30–18:00")}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {[
              { Icon:Train, label:"Railway",  val:`${transportData.railway.load}%` },
              { Icon:Bus,   label:"Bus",      val:`${transportData.bus.load}%`     },
              { Icon:Car,   label:"Shuttle",  val:`${transportData.shuttle.load}%` },
              { Icon:MapPin,label:"Parking",  val:`${transportData.parking.load}%` },
            ].map(({ Icon, label, val }, i) => {
              const c = googleColor(i);
              return (
                <div key={label}
                  className="rounded-xl px-3 py-2 text-center min-w-[80px] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                  style={{ background:c.bgLight, border:`1px solid ${c.border}` }}>
                  <Icon size={14} className="mx-auto mb-1" style={{ color:c.text }} />
                  <div className="text-xl font-black" style={{ color:c.text }}>{val}</div>
                  <div className="text-[9px] opacity-70 font-black uppercase tracking-wide" style={{ color:c.text }}>{t(label)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Mobility Flow + Corridor Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ChartCard title="Pilgrim Mobility Flow — Volume by Mode"
            subtitle="Hourly passenger volume across railway, bus and shuttle" live>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={transportData.mobilityFlow} margin={{ top:5, right:10, left:-10, bottom:0 }}>
                <defs>
                  {[
                    { id:"railGrad", color:chartColor(0) }, { id:"busGrad", color:chartColor(1) }, { id:"shuGrad", color:chartColor(2) }
                  ].map(g => (
                    <linearGradient key={g.id} id={g.id} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={g.color} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={g.color} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="time" tick={axisTick} />
                <YAxis tickFormatter={v=>`${(v/1000).toFixed(0)}K`} tick={axisTick} />
                <Tooltip content={<ChartTooltip formatter={(v, n) => [v.toLocaleString(), n]} />} />
                <Legend wrapperStyle={legendStyle} />
                <Area type="monotone" dataKey="railway" fill="url(#railGrad)" stroke={chartColor(0)} strokeWidth={2.5} name={t("Railway")} />
                <Area type="monotone" dataKey="bus"     fill="url(#busGrad)"  stroke={chartColor(1)} strokeWidth={2.5} name={t("Bus")} />
                <Area type="monotone" dataKey="shuttle" fill="url(#shuGrad)"  stroke={chartColor(2)} strokeWidth={2}   name={t("Shuttle")} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <ChartCard title="Traffic Corridors" subtitle="Key route saturation & AI prediction">
          <div className="space-y-2.5">
            {trafficCorridors.map((c, i) => (
              <motion.div key={c.name}
                initial={{ opacity:0, x:6 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.06 }}
                className="p-2.5 rounded-xl border border-slate-200 hover:shadow-card-hover transition-shadow"
              >
                <div className="flex items-center justify-between mb-1 gap-2">
                  <span className="text-[10px] font-bold text-slate-800 truncate flex-1">{t(c.name)}</span>
                  <span className={corridorStatusBadge(c.status)}>{t(c.status)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <motion.div className="h-1.5 rounded-full" style={{
                    width:`${c.load}%`,
                    background: statusHex(thresholdStatus(c.load, LOAD_BANDS_4))
                  }} initial={{ width:0 }} animate={{ width:`${c.load}%` }} transition={{ duration:0.8, delay: i * 0.06 }} />
                </div>
                <div className="flex items-center justify-between mt-1 text-[9px] text-slate-500">
                  <span>{c.dist}</span>
                  <span className="font-bold text-slate-700">{c.load}%</span>
                </div>
                <div className="text-[9px] text-amber-600 font-semibold mt-0.5">{t(c.prediction)}</div>
              </motion.div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Inbound/Outbound + Delay Causes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Inbound vs Outbound Balance"
          subtitle="Pilgrims arriving vs departing across all modes — hourly" live>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={INBOUND_OUTBOUND} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <defs>
                <linearGradient id="inGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor(0)} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={chartColor(0)} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="outGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor(2)} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={chartColor(2)} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="time" tick={axisTick} />
              <YAxis tickFormatter={v=>`${(v/1000).toFixed(0)}K`} tick={axisTick} />
              <Tooltip content={<ChartTooltip formatter={(v, n) => [v.toLocaleString(), n]} />} />
              <Legend wrapperStyle={legendStyle} />
              <Area type="monotone" dataKey="inbound"  fill="url(#inGrad)"  stroke={chartColor(0)} strokeWidth={2.5} name={t("Inbound")} />
              <Area type="monotone" dataKey="outbound" fill="url(#outGrad)" stroke={chartColor(2)} strokeWidth={2.5} name={t("Outbound")} />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-100 text-center text-xs">
            <div><div className="text-lg font-black text-blue-700">6.84L</div><div className="text-[10px] text-slate-500">{t("Pilgrims In")}</div></div>
            <div><div className="text-lg font-black text-emerald-600">↑ 16:00</div><div className="text-[10px] text-slate-500">{t("Outbound Peak")}</div></div>
            <div><div className="text-lg font-black text-amber-600">+{netPresentLakh}L</div><div className="text-[10px] text-slate-500">{t("Net Present")}</div></div>
          </div>
        </ChartCard>

        <ChartCard title="Transport Delay Cause Analysis"
          subtitle="Root cause of delays across all transport modes — today">
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="50%" height={200}>
              <PieChart>
                <Pie data={DELAY_CAUSES} dataKey="value" cx="50%" cy="50%"
                  outerRadius={80} innerRadius={44} label={({ value }) => `${value}%`} labelLine={false}>
                  {DELAY_CAUSES.map((e, i) => <Cell key={i} fill={chartColor(i)} />)}
                </Pie>
                <Tooltip content={<ChartTooltip formatter={(v, n) => [`${v}%`, n]} />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2 flex-1">
              {DELAY_CAUSES.map((d, i) => (
                <div key={d.name} className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background:chartColor(i) }} />
                  <span className="text-[9px] text-slate-700 flex-1 leading-tight">{t(d.name)}</span>
                  <span className="text-[10px] font-black text-slate-800">{d.value}%</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-3 gap-2 text-center text-xs">
            <div><div className="text-base font-black text-red-600">{transportData.bus.delayed + transportData.railway.delayed + transportData.shuttle.delayed}</div><div className="text-[10px] text-slate-500">{t("Total Delays")}</div></div>
            <div><div className="text-base font-black text-amber-600">22 min</div><div className="text-[10px] text-slate-500">{t("Avg Delay")}</div></div>
            <div><div className="text-base font-black text-emerald-600">78%</div><div className="text-[10px] text-slate-500">{t("On-time rate")}</div></div>
          </div>
        </ChartCard>
      </div>

      {/* Fleet Board tabs */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-sm font-black text-slate-900">{t("Live Fleet & Infrastructure Board")}</h3>
          <div className="flex gap-1 flex-wrap">
            {[
              {id:"buses",label:"Bus Dispatch"},
              {id:"shuttle",label:"Shuttle Circuits"},
              {id:"signals",label:"Signal Overrides"},
              {id:"pedestrian",label:"Pedestrian Flows"},
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`text-[9px] font-black px-3 py-1.5 rounded-lg transition-colors ${
                  activeTab === tab.id ? "gov-gradient text-white" : "bg-slate-100 text-slate-600"
                }`}>{t(tab.label)}</button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === "buses" && (
            <motion.div key="buses" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {BUS_DISPATCH.map((b, i) => (
                <motion.div key={b.busNo}
                  initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.05 }}
                  className={`p-3 rounded-xl border text-xs ${statusClasses(busStatusBucket(b.status))}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-black font-mono text-sm">{b.busNo.replace("MSRTC ","")}</span>
                    <span className="badge badge-info text-[8px]">{t(b.type)}</span>
                  </div>
                  <div className="text-[10px] font-semibold text-slate-700 mb-0.5 leading-tight">{t(b.route)}</div>
                  <div className="flex items-center gap-1.5 text-[9px] text-slate-500 mb-1.5">
                    <span>{t(b.bay)}</span>
                    {b.eta !== "Done" && <span className="font-bold text-blue-700">{t("ETA")} {b.eta}</span>}
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
                    <div className="h-1.5 rounded-full" style={{
                      width:`${b.load}%`,
                      background: statusHex(thresholdStatus(b.load, LOAD_BANDS_3))
                    }} />
                  </div>
                  <div className="text-[9px] text-slate-500 mt-0.5 text-right">{b.load}% {t("load")}</div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {activeTab === "shuttle" && (
            <motion.div key="shuttle" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              className="space-y-2.5">
              {SHUTTLE_CIRCUITS.map((s, i) => (
                <motion.div key={s.route}
                  initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                  className={`p-3 rounded-xl border flex items-center gap-4 flex-wrap ${
                    s.status === "Delayed" ? "border-amber-200 bg-amber-50/20" : "border-slate-200"
                  }`}
                >
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-black text-slate-900">{t(s.route)}</span>
                      <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${
                        s.status === "Delayed" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
                      }`}>{t(s.status)}</span>
                    </div>
                    <div className="flex gap-3 text-[9px] text-slate-500 flex-wrap">
                      <span>{t("Every")} <strong className="text-slate-700">{s.frequency}</strong></span>
                      <span><strong className="text-blue-700">{s.vehicles}</strong> {t("vehicles")}</span>
                      <span>{t("Avg")} {s.avgTime}</span>
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    <div className="text-xl font-black text-blue-700">{s.passengers.toLocaleString()}</div>
                    <div className="text-[9px] text-slate-500">{t("passengers today")}</div>
                  </div>
                </motion.div>
              ))}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-3 border-t border-slate-100 text-center text-xs">
                <div><div className="text-xl font-black text-blue-700">{SHUTTLE_CIRCUITS.reduce((s,r)=>s+r.vehicles,0)}</div><div className="text-[10px] text-slate-500">{t("Total Vehicles")}</div></div>
                <div><div className="text-xl font-black text-emerald-600">{SHUTTLE_CIRCUITS.reduce((s,r)=>s+r.passengers,0).toLocaleString()}</div><div className="text-[10px] text-slate-500">{t("Passengers")}</div></div>
                <div><div className="text-xl font-black text-amber-600">{SHUTTLE_CIRCUITS.filter(r=>r.status==="Delayed").length}</div><div className="text-[10px] text-slate-500">{t("Delayed")}</div></div>
              </div>
            </motion.div>
          )}

          {activeTab === "signals" && (
            <motion.div key="signals" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              className="space-y-2.5">
              {SIGNAL_OVERRIDES.map((s, i) => (
                <motion.div key={s.junction}
                  initial={{ opacity:0, y:3 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.07 }}
                  className={`p-3 rounded-xl border flex items-center gap-4 flex-wrap ${
                    s.active ? "border-amber-200 bg-amber-50/20" : "border-slate-200"
                  }`}
                >
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 mb-0.5">
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${s.active ? "bg-amber-500 live-dot" : "bg-slate-300"}`} />
                      <span className="text-xs font-black text-slate-900">{t(s.junction)}</span>
                    </div>
                    <div className="text-[10px] text-slate-500">{t(s.reason)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-bold text-amber-700">{t(s.override)}</div>
                    <div className={`text-[9px] font-black mt-0.5 ${s.active ? "text-amber-600" : "text-slate-400"}`}>
                      {s.active ? t("ACTIVE") : t("Normal")}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {activeTab === "pedestrian" && (
            <motion.div key="pedestrian" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
              className="space-y-3">
              {PEDESTRIAN_FLOWS.map((p, i) => {
                const bucket = pedestrianBucket(p.status);
                return (
                <motion.div key={p.corridor}
                  initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                  className={`p-3 rounded-xl border ${
                    bucket === "good" ? "border-slate-200" : `${STATUS[bucket].border} ${STATUS[bucket].bg}/20`
                  }`}
                >
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Bike size={12} className="text-blue-600 flex-shrink-0" />
                        <Link to="/crowd-intelligence" className="text-xs font-black text-slate-900 hover:text-blue-700 hover:underline underline-offset-2">{t(p.corridor)}</Link>
                        <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-full ${STATUS[bucket].bg} ${STATUS[bucket].text}`}>{t(p.status)}</span>
                      </div>
                      <div className="text-[9px] text-blue-700 font-bold mt-0.5">{t("Action")}: {t(p.action)}</div>
                    </div>
                    <div className="flex gap-4 text-center text-xs flex-shrink-0">
                      <div><div className="font-black text-slate-900">{p.density}</div><div className="text-[9px] text-slate-500">{t("Density")}</div></div>
                      <div><div className="font-black text-blue-700">{p.flow.toLocaleString()}</div><div className="text-[9px] text-slate-500">{t("Flow/hr")}</div></div>
                    </div>
                  </div>
                </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Parking + Train */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ChartCard title="Parking Zone Utilisation"
          subtitle="Used vs capacity · EV bays · overflow redirect · shuttle link">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={PARKING_DATA} layout="vertical" margin={{ left:10, right:40 }}>
              <XAxis type="number" tick={axisTick} domain={[0,3500]} />
              <YAxis type="category" dataKey="name" tick={{ ...axisTick, fontSize:9 }} width={140} tickFormatter={t} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Bar dataKey="total" fill="#E2E8F0" radius={[0,4,4,0]} name={t("Capacity")} />
              <Bar dataKey="used"  fill={chartColor(0)} radius={[0,4,4,0]} name={t("Used")} />
            </BarChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-3 pt-3 border-t border-slate-100">
            {PARKING_DATA.map((p, i) => {
              const bucket = thresholdStatus(p.pct, LOAD_BANDS_3);
              return (
              <div key={p.name} className="flex items-center gap-2 text-[9px]">
                <span className="text-slate-600 w-36 truncate flex-shrink-0">{t(p.name.replace("P1 — ","P1·").replace("P2 — ","P2·").replace("P3 — ","P3·").replace("P4 — ","P4·").replace("P5 — ","P5·"))}</span>
                <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-1.5 rounded-full" style={{
                    width:`${p.pct}%`,
                    background: statusHex(bucket)
                  }} />
                </div>
                <span className={`font-black w-7 text-right flex-shrink-0 ${STATUS[bucket].text}`}>{p.pct}%</span>
                <span className="text-blue-600 flex-shrink-0">EV:{p.ev}</span>
              </div>
              );
            })}
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2 border-t border-slate-100 text-center text-xs">
            <div><div className="text-base font-black text-slate-900">{parkingSpacesTotal.toLocaleString()}</div><div className="text-[9px] text-slate-500">{t("Total Spaces")}</div></div>
            <div><div className="text-base font-black text-blue-700">{parkingUsedTotal.toLocaleString()}</div><div className="text-[9px] text-slate-500">{t("Occupied")}</div></div>
            <div><div className="text-base font-black text-amber-600">{parkingEvTotal}</div><div className="text-[9px] text-slate-500">{t("EV Bays")}</div></div>
          </div>
        </ChartCard>

        <ChartCard title="Train Schedule — Nashik Road Station"
          subtitle="Arrivals, platform, load · Shahi Snan special trains included">
          <div className="space-y-2">
            {trainSchedule.map((tr, i) => (
              <motion.div key={tr.train}
                initial={{ opacity:0, x:8 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.07 }}
                className={`p-3 rounded-xl border transition-all ${
                  tr.status === "Delayed" ? "border-red-200 bg-red-50" : "border-slate-200 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="text-xs font-bold text-slate-800">{tr.train}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{t("From")} {t(tr.from)} · {t("Platform")} {tr.platform}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-black text-slate-900">{tr.arrival}</div>
                    <span className={tr.status === "On Time" ? "badge badge-success" : "badge badge-critical"}>{t(tr.status)}</span>
                  </div>
                </div>
                <div className="mt-1.5 flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-1.5 rounded-full" style={{
                      width:`${tr.load}%`,
                      background: statusHex(thresholdStatus(tr.load, LOAD_BANDS_3))
                    }} />
                  </div>
                  <span className="text-[10px] font-bold text-slate-700">{tr.load}% {t("load")}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Park & Ride + Load Forecast */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Park & Ride Zones — Live Utilisation"
          subtitle="4 park-and-ride hubs with dedicated shuttle links to all Kumbh zones">
          <div className="space-y-3">
            {PARK_AND_RIDE.map((p, i) => {
              const bucket = thresholdStatus(p.pct, LOAD_BANDS_2);
              return (
                <motion.div key={p.id}
                  initial={{ opacity:0, x:-4 }} animate={{ opacity:1, x:0 }} transition={{ delay: i * 0.08 }}
                  className="p-3 rounded-xl border border-slate-200 hover:shadow-card-hover transition-shadow"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1">
                      <div className="text-xs font-black text-slate-900">{t(p.name)}</div>
                      <div className="text-[9px] text-slate-500 mt-0.5">→ {t(p.dest)}</div>
                    </div>
                    <div className="text-right text-[10px]">
                      <div className="font-black text-slate-800">{p.used.toLocaleString()} / {p.capacity.toLocaleString()}</div>
                      <div className={`font-black ${STATUS[bucket].text}`}>{p.pct}%</div>
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-1.5">
                    <motion.div className="h-2 rounded-full" style={{ background: statusHex(bucket) }}
                      initial={{ width:0 }} animate={{ width:`${p.pct}%` }} transition={{ duration:0.8 }} />
                  </div>
                  <div className="flex items-center gap-3 text-[9px] text-slate-500 flex-wrap">
                    <span>{t("Shuttle")}: <strong className="text-blue-700">{p.shuttle}</strong></span>
                    <span>{t("Every")} <strong className="text-slate-700">{p.freq}</strong></span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </ChartCard>

        <ChartCard title="Transport Load Forecast — 4-Hour Window"
          subtitle="Predicted load % for railway, bus, shuttle, parking (AI model)" live>
          <div className="mb-2 px-1">
            <span className="badge badge-warning text-[9px]">{t("AI forecast — peak load 16:00 on all modes")}</span>
          </div>
          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={LOAD_FORECAST} margin={{ top:5, right:10, left:-10, bottom:0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="time" tick={axisTick} />
              <YAxis tick={axisTick} unit="%" domain={[50,100]} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={legendStyle} />
              <Line type="monotone" dataKey="railway" stroke={chartColor(0)} strokeWidth={2.5} strokeDasharray="5 3" dot={{ r:3 }} name={t("Railway %")} />
              <Line type="monotone" dataKey="bus"     stroke={chartColor(1)} strokeWidth={2.5} strokeDasharray="5 3" dot={{ r:3 }} name={t("Bus %")} />
              <Line type="monotone" dataKey="shuttle" stroke={chartColor(2)} strokeWidth={2}   strokeDasharray="5 3" dot={{ r:3 }} name={t("Shuttle %")} />
              <Line type="monotone" dataKey="parking" stroke={chartColor(3)} strokeWidth={2}   strokeDasharray="5 3" dot={{ r:3 }} name={t("Parking %")} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Night Transport Plan */}
      <ChartCard title="Night Transport Plan — 22:00 to 06:00"
        subtitle="Post-event departure services — Kumbh 2027 special night schedule for all pilgrims">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {NIGHT_TRANSPORT.map((n, i) => (
            <motion.div key={n.service}
              initial={{ opacity:0, y:4 }} animate={{ opacity:1, y:0 }} transition={{ delay: i * 0.07 }}
              className="p-3 rounded-xl border border-slate-200 hover:shadow-card-hover transition-shadow"
            >
              <div className="flex items-center gap-1.5 mb-2">
                {n.service.includes("Train") ? <Train size={12} className="text-blue-600" /> :
                 n.service.includes("shuttle") ? <Car size={12} className="text-emerald-600" /> :
                 <Bus size={12} className="text-amber-600" />}
                <span className={`text-[8px] font-black px-1 py-0.5 rounded-full ${
                  n.status === "Active" ? "bg-emerald-100 text-emerald-700" :
                  n.status === "Reserved" ? "bg-violet-100 text-violet-700" : "bg-blue-100 text-blue-700"
                }`}>{t(n.status)}</span>
              </div>
              <div className="text-[10px] font-black text-slate-900 mb-1 leading-tight">{t(n.service)}</div>
              <div className="text-[9px] text-slate-500">{t("Departs")}: <strong className="text-blue-700">{n.time}</strong></div>
              <div className="text-[9px] text-slate-500">{n.platform ? `${t("Platform")} ${n.platform}` : t(n.bay)}</div>
              {n.seats !== "Continuous" && n.seats !== "—" && (
                <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-1.5 rounded-full bg-blue-500" style={{
                    width: typeof n.booked === "number" ? `${Math.round(n.booked/n.seats*100)}%` : "0%"
                  }} />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </ChartCard>
    </div>
  );
}





